import { useEffect, useState, useCallback } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Dumbbell, FileDown, X, Layout, BookOpen, Save, Paperclip, FileText, ImageIcon, Upload, QrCode } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Dialog as QrDialog, DialogContent as QrDialogContent, DialogHeader as QrDialogHeader, DialogTitle as QrDialogTitle, DialogDescription as QrDialogDescription } from "../components/ui/dialog";
import DrillBoardDialog from "../components/trainings/DrillBoardDialog";
import ExercisePickerDialog from "../components/trainings/ExercisePickerDialog";
import { DatePicker } from "../components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { exportTrainingPdf } from "../components/trainings/trainingPdf";

const empty = {
  title: "",
  date: "",
  start_time: "",
  field_id: "",
  location: "",
  duration_min: 90,
  objectives: "",
  drills: [],
};

const newDrill = () => ({ id: crypto.randomUUID(), name: "", duration: 15, description: "" });

export default function TrainingsPage() {
  const { activeTeam, season } = useTeams();
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [drillBoardOpen, setDrillBoardOpen] = useState(false);
  const [drillBoardIndex, setDrillBoardIndex] = useState(-1);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTargetIndex, setPickerTargetIndex] = useState(-1);
  const [qrFor, setQrFor] = useState(null);
  const [qrToken, setQrToken] = useState(null);
  const [qrCount, setQrCount] = useState(0);

  // Generate token whenever the QR dialog opens for a training
  useEffect(() => {
    if (!qrFor) { setQrToken(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/trainings/${qrFor.id}/attendance-token`);
        if (!cancelled) setQrToken(data);
      } catch (e) {
        if (!cancelled) toast.error("Errore generazione QR");
      }
    })();
    return () => { cancelled = true; };
  }, [qrFor]);

  // Poll attendance count while QR dialog is open
  useEffect(() => {
    if (!qrFor) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/teams/${activeTeam.id}/trainings`, { params: { season } });
        const updated = data.find((x) => x.id === qrFor.id);
        const c = updated?.attendance?.present?.length || 0;
        setQrCount(c);
        setItems(data);
      } catch (e) { /* ignore polling errors */ }
    }, 3000);
    return () => clearInterval(t);
  }, [qrFor, activeTeam, season]);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [trRes, fRes] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/trainings`, { params: { season } }),
      api.get(`/teams/${activeTeam.id}/fields`).catch(() => ({ data: [] })),
    ]);
    setItems(trRes.data);
    setFields(fRes.data || []);
  }, [activeTeam, season]);

  useEffect(() => {
    load();
  }, [load]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.title || !form.date) return toast.error("Titolo e data richiesti");
    const payload = { ...form, team_id: activeTeam.id, season, duration_min: Number(form.duration_min) || 90 };
    if (editingId) await api.put(`/trainings/${editingId}`, payload);
    else await api.post(`/trainings`, payload);
    toast.success("Allenamento salvato");
    setOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const newOne = () => {
    setForm({ ...empty, drills: [newDrill()] });
    setEditingId(null);
    setOpen(true);
  };

  const editOne = (t) => {
    setForm({ ...empty, ...t });
    setEditingId(t.id);
    setOpen(true);
  };

  const remove = async (t) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/trainings/${t.id}`);
    load();
  };

  const updateDrill = (i, patch) => {
    const drills = form.drills.map((d, idx) => (idx === i ? { ...d, ...patch } : d));
    setForm({ ...form, drills });
  };

  const removeDrill = (i) => {
    setForm({ ...form, drills: form.drills.filter((_, idx) => idx !== i) });
  };

  const openDrillBoard = (i) => {
    setDrillBoardIndex(i);
    setDrillBoardOpen(true);
  };

  const saveDrillDiagram = (diagram) => {
    if (drillBoardIndex < 0) return;
    const drills = form.drills.map((d, idx) =>
      idx === drillBoardIndex ? { ...d, diagram } : d
    );
    setForm({ ...form, drills });
  };

  const openPicker = (i) => {
    setPickerTargetIndex(i);
    setPickerOpen(true);
  };

  const applyFromLibrary = (template) => {
    if (pickerTargetIndex < 0) return;
    const drills = form.drills.map((d, idx) =>
      idx === pickerTargetIndex
        ? {
            ...d,
            name: template.name,
            duration: template.duration,
            description: template.description,
            diagram: template.diagram,
            image_url: template.image_url || d.image_url,
          }
        : d
    );
    setForm({ ...form, drills });
    toast.success("Esercizio caricato");
  };

  const saveDrillToLibrary = async (drill) => {
    if (!drill.name?.trim()) {
      toast.error("Aggiungi un nome all'esercizio prima di salvarlo");
      return;
    }
    try {
      await api.post(`/exercises`, {
        team_id: activeTeam.id,
        name: drill.name,
        category: null,
        duration: Number(drill.duration) || 15,
        description: drill.description || "",
        diagram: drill.diagram || null,
        image_url: drill.image_url || null,
      });
      toast.success("Esercizio salvato in libreria");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const uploadDrillImage = async (drillIdx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateDrill(drillIdx, { image_url: data.url });
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    }
  };

  const uploadAttachment = async (training, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/trainings/${training.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Allegato caricato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    }
  };

  const removeAttachment = async (training, attachmentId) => {
    if (!window.confirm("Eliminare allegato?")) return;
    await api.delete(`/trainings/${training.id}/attachments/${attachmentId}`);
    load();
  };

  const exportPdf = (t) => {
    exportTrainingPdf(t, activeTeam).catch((err) => {
      console.warn("PDF export failed", err);
      toast.error("Errore generazione PDF");
    });
  };

  return (
    <div className="space-y-6" data-testid="trainings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Allenamenti</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Sedute & esercizi</h1>
        </div>
        <Button
          onClick={newOne}
          className="rounded-full bg-sky-500 hover:bg-sky-600"
          data-testid="new-training-btn"
        >
          <Plus size={16} className="mr-1" /> Nuovo
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((t) => (
          <Card key={t.id} className="p-5 border-slate-200" data-testid={`training-${t.id}`}>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="h-12 w-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
                <Dumbbell size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold">{t.title}</div>
                <div className="text-sm text-slate-500">
                  {t.date} · {t.duration_min || 90} min · {t.drills?.length || 0} esercizi
                  {t.location && ` · ${t.location}`}
                  {t.attachments?.length > 0 && (
                    <>
                      {" "}
                      ·{" "}
                      <span className="inline-flex items-center gap-1 text-violet-600 font-medium">
                        <Paperclip size={12} /> {t.attachments.length} allegati
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <label
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-medium cursor-pointer hover:bg-slate-50"
                  data-testid={`upload-attachment-${t.id}`}
                >
                  <Upload size={14} /> Allega
                  <input
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      uploadAttachment(t, f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <Button size="sm" variant="outline" onClick={() => exportPdf(t)}>
                  <FileDown size={14} className="mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => setQrFor(t)} data-testid={`qr-attendance-${t.id}`}>
                  <QrCode size={14} className="mr-1" /> QR Presenze
                </Button>
                <Button size="icon" variant="ghost" onClick={() => editOne(t)}>
                  <Pencil size={15} />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(t)}>
                  <Trash2 size={15} className="text-red-500" />
                </Button>
              </div>
            </div>
            {t.attachments?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {t.attachments.map((a) => {
                  const isImg = (a.content_type || "").startsWith("image/");
                  const Icon = isImg ? ImageIcon : FileText;
                  return (
                    <div
                      key={a.id}
                      className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-xs"
                      data-testid={`attachment-${a.id}`}
                    >
                      <Icon size={14} className="text-violet-600" />
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-slate-700 hover:text-sky-600 truncate max-w-[180px]"
                        title={a.filename}
                      >
                        {a.filename}
                      </a>
                      <span className="text-slate-400 tabular-nums">
                        {Math.round((a.size || 0) / 1024)} KB
                      </span>
                      <button
                        onClick={() => removeAttachment(t, a.id)}
                        className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
                        title="Elimina"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-10 text-center text-slate-500 border-dashed">
            Nessun allenamento. Pianifica la prima seduta!
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica allenamento" : "Nuovo allenamento"}</DialogTitle>
          <DialogDescription className="sr-only">Compila i dettagli della seduta e degli esercizi.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Titolo</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="training-title"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Data</Label>
                <DatePicker
                  value={form.date}
                  onChange={(v) => setForm({ ...form, date: v })}
                />
              </div>
              <div>
                <Label>Inizio</Label>
                <Input
                  type="time"
                  value={form.start_time || ""}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="training-start-time"
                />
              </div>
              <div>
                <Label>Durata (min)</Label>
                <Input
                  type="number"
                  value={form.duration_min}
                  onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                />
              </div>
              <div>
                <Label>Campo</Label>
                <Select
                  value={form.field_id || "__none__"}
                  onValueChange={(v) => {
                    const fid = v === "__none__" ? "" : v;
                    const f = fields.find((x) => x.id === fid);
                    setForm({
                      ...form,
                      field_id: fid,
                      location: f ? f.name : form.location,
                    });
                  }}
                >
                  <SelectTrigger data-testid="training-field-select">
                    <SelectValue placeholder="Nessun campo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessun campo</SelectItem>
                    {fields.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Luogo (libero)</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Sovrascrive il nome del campo, opzionale"
              />
            </div>
            <div>
              <Label>Obiettivi tecnico-tattici</Label>
              <Textarea
                rows={2}
                value={form.objectives}
                onChange={(e) => setForm({ ...form, objectives: e.target.value })}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Esercizi</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setForm({ ...form, drills: [...form.drills, newDrill()] })}
                >
                  <Plus size={14} className="mr-1" /> Aggiungi
                </Button>
              </div>
              <div className="space-y-3">
                {form.drills.map((d, i) => (
                  <Card key={d.id || `drill-${i}`} className="p-4 border-slate-200">
                    <div className="flex items-start gap-2">
                      <span className="font-heading font-bold text-slate-400 text-lg w-6">
                        {i + 1}
                      </span>
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Nome esercizio"
                            value={d.name}
                            className="col-span-2"
                            onChange={(e) => updateDrill(i, { name: e.target.value })}
                          />
                          <Input
                            type="number"
                            placeholder="Min"
                            value={d.duration}
                            onChange={(e) => updateDrill(i, { duration: Number(e.target.value) })}
                          />
                        </div>
                        <Textarea
                          rows={2}
                          placeholder="Descrizione, obiettivo, ripetizioni..."
                          value={d.description}
                          onChange={(e) => updateDrill(i, { description: e.target.value })}
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openDrillBoard(i)}
                            data-testid={`drill-board-${i}`}
                          >
                            <Layout size={14} className="mr-1" />
                            {d.diagram?.pieces?.length
                              ? `Lavagna (${d.diagram.pieces.length})`
                              : "Disegna"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openPicker(i)}
                            data-testid={`drill-from-library-${i}`}
                          >
                            <BookOpen size={14} className="mr-1" /> Da libreria
                          </Button>
                          <label
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-medium cursor-pointer hover:bg-slate-50"
                            data-testid={`drill-image-upload-${i}`}
                          >
                            <ImageIcon size={14} /> {d.image_url ? "Cambia foto" : "Foto"}
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                uploadDrillImage(i, f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {d.image_url && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => updateDrill(i, { image_url: null })}
                              title="Rimuovi foto"
                            >
                              <X size={13} /> Foto
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => saveDrillToLibrary(d)}
                            data-testid={`drill-to-library-${i}`}
                            title="Salva in libreria"
                          >
                            <Save size={14} className="mr-1" /> Salva
                          </Button>
                        </div>
                        {d.image_url && (
                          <a href={d.image_url} target="_blank" rel="noreferrer" className="block">
                            <img
                              src={d.image_url}
                              alt={d.name || "esercizio"}
                              className="max-h-32 rounded-lg border border-slate-200 object-cover"
                            />
                          </a>
                        )}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeDrill(i)}>
                        <X size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-training-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DrillBoardDialog
        open={drillBoardOpen}
        onOpenChange={setDrillBoardOpen}
        drill={drillBoardIndex >= 0 ? form.drills[drillBoardIndex] : null}
        onSave={saveDrillDiagram}
      />

      <ExercisePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={applyFromLibrary}
      />

      <QrDialog open={!!qrFor} onOpenChange={(o) => { if (!o) setQrFor(null); }}>
        <QrDialogContent className="sm:max-w-md">
          <QrDialogHeader>
            <QrDialogTitle>QR Presenze · {qrFor?.title}</QrDialogTitle>
            <QrDialogDescription className="sr-only">Codice QR per registrazione presenze allenamento</QrDialogDescription>
          </QrDialogHeader>
          <div className="text-center space-y-3">
            <p className="text-xs text-slate-500">I giocatori scansionano questo QR per registrare la presenza. Token valido 30 minuti.</p>
            {qrToken ? (
              <>
                <div className="inline-block bg-white p-4 rounded-2xl border-2 border-slate-200" data-testid="qr-svg-container">
                  <QRCodeSVG value={`${window.location.origin}/presenza/${qrToken.token}`} size={240} />
                </div>
                <div className="text-xs text-slate-500 break-all">
                  {window.location.origin}/presenza/{qrToken.token}
                </div>
                <div className="text-sm font-semibold text-emerald-600" data-testid="qr-count">
                  {qrCount} presenti
                </div>
              </>
            ) : (
              <p className="text-slate-400 py-10">Generazione...</p>
            )}
          </div>
        </QrDialogContent>
      </QrDialog>
    </div>
  );
}
